import { Button, TooltipShortcut } from "@itk/mui-components";
import { Stack, Typography, useTheme } from "@mui/material";
import { connect } from "react-redux";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { ConfiguratorEditorLayout } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { ROUTES_URL } from "src/utils/constants";
import { RootState } from "src/store";
import { bulkConfigureProductsActions, TProductTypeAttributesRequest } from "./bulk-configure-products-store";
import { buildProductTypeLines, TSelectedProduct } from "./bulk-configure-products-utils";
import { BulkAttributeSection, BulkAttributeSectionLoading, ProductTypeListSection, ProductTypeListSectionLoading } from "./components";

const mapStateToProps = (state: RootState) => ({
  selectedProducts: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedProducts,
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState,
  productTypeAttributesState: state.pageReducer.bulkConfigureProductsReducer.productTypeAttributesState,
  selectedProductTypeId: state.pageReducer.bulkConfigureProductsReducer.selectedProductTypeId
});

const ownActions = {
  getProductTypeAttributesRequest: bulkConfigureProductsActions.getProductTypeAttributesRequest,
  setSelectedProductTypeId: bulkConfigureProductsActions.setSelectedProductTypeId,
  clear: bulkConfigureProductsActions.clear
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const BulkConfigureProductsBase = (props: Props) => {
  const { selectedProducts, configurationsState, productTypeAttributesState, selectedProductTypeId, getProductTypeAttributesRequest, setSelectedProductTypeId, clear } = props;

  const theme = useTheme();
  const navigate = useNavigate();
  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const { projectId = "" } = useParams<{ projectId: string }>();

  // Ids only — names come from the definitions response
  const products = useMemo((): TSelectedProduct[] => {
    const selectedIds = new Set(selectedProducts);

    return (configurationsState?.configurations ?? [])
      .flatMap(configuration => configuration.systemProductConfigurationItems ?? [])
      .filter(item => selectedIds.has(item.systemProductConfigurationItemId ?? ""))
      .map(item => ({
        systemProductConfigurationItemId: item.systemProductConfigurationItemId ?? "",
        productTypeId: item.productTypeId ?? ""
      }))
      .filter(product => product.systemProductConfigurationItemId !== "" && product.productTypeId !== "");
  }, [configurationsState, selectedProducts]);

  // The item ids ride along so the server can fill in the values the whole selection already agrees on
  const attributesRequest = useMemo(
    (): TProductTypeAttributesRequest => ({
      productTypeIds: [...new Set(products.map(product => product.productTypeId))],
      systemProductConfigurationItemIds: products.map(product => product.systemProductConfigurationItemId)
    }),
    [products]
  );

  // Selection lives in Redux, not the URL, so a refresh lands here with nothing to edit
  useEffect(() => {
    if (selectedProducts.length === 0) navigate(ROUTES_URL.CONFIGURATOR.build(projectId), { replace: true });
  }, [selectedProducts]);

  useEffect(() => {
    if (attributesRequest.productTypeIds.length > 0) getProductTypeAttributesRequest(attributesRequest);
  }, [attributesRequest]);

  useEffect(() => {
    return () => {
      clear();
    };
  }, []);

  const lines = useMemo(() => {
    const definitions = productTypeAttributesState.loading === "has-loaded" ? productTypeAttributesState.data : [];
    return buildProductTypeLines(products, definitions);
  }, [products, productTypeAttributesState]);

  useEffect(() => {
    if (!selectedProductTypeId && lines.length > 0) setSelectedProductTypeId(lines[0].productTypeId);
  }, [lines, selectedProductTypeId]);

  const selectedLine = lines.find(line => line.productTypeId === selectedProductTypeId);

  // "not-started" counts as loading — the request goes out from an effect, one render later
  const isLoadingAttributes = productTypeAttributesState.loading === "not-started" || productTypeAttributesState.loading === "is-loading";

  const handleClose = () => navigate(ROUTES_URL.CONFIGURATOR.build(projectId));

  return (
    <ConfiguratorEditorLayout
      sidebar={isLoadingAttributes ? <ProductTypeListSectionLoading /> : <ProductTypeListSection lines={lines} selectedProductTypeId={selectedProductTypeId} onSelect={setSelectedProductTypeId} />}
      attributePanel={
        isLoadingAttributes ? (
          <BulkAttributeSectionLoading />
        ) : selectedLine ? (
          <BulkAttributeSection line={selectedLine} />
        ) : (
          <Typography px={2.4} py={2} variant="body2" color={theme.palette.customColors.darkGrey}>
            {configuratorPageT.noProductSelected}
          </Typography>
        )
      }
      footer={
        <>
          <Stack />
          <TooltipShortcut shortcut={{ action: handleClose, shortCutKey: "g", modifier: "alt" }}>
            <Button variant="contained" t={genericTranslationsT} k={genericTranslationsK} field="saveAndClose" onClick={handleClose} />
          </TooltipShortcut>
        </>
      }
    />
  );
};

export const BulkConfigureProducts = connect(mapStateToProps, ownActions)(BulkConfigureProductsBase);
